import { useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, X, Check, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { matchesAll } from "@/lib/trFold";

/**
 * DefinitionSelect — Select bound to a `/definitions/{kind}` taxonomy.
 *
 * • `items` is the current list (id/name objects).
 * • On "+ Yeni Ekle" the user types a name inline; we POST /api/definitions/{kind},
 *   refresh via `onCreated(created)` so parent can append + re-render, then
 *   automatically select the newly-created name.
 *
 * Props:
 *   label       : string
 *   required    : boolean (adds red asterisk + red border when empty)
 *   value       : selected name (string)
 *   onChange    : (name: string) => void
 *   items       : Array<{id, name, ...}>
 *   kind        : definition kind (e.g. 'fabric_name', 'composition')
 *   testId      : base data-testid
 *   placeholder : select placeholder
 *   addLabel    : "Yeni Kumaş Adı Ekle" — button text
 *   onCreated   : (created: {id, name}) => void   — parent updates its items[]
 */
export default function DefinitionSelect({
  label,
  required = false,
  value = "",
  onChange,
  items = [],
  kind,
  testId = "def-select",
  placeholder = "Seçin",
  addLabel = "Yeni Ekle",
  onCreated,
}) {
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    return items.filter((t) => matchesAll(q, [t.name]));
  }, [items, q]);

  const startAdd = () => {
    setAdding(true);
    setNewName("");
  };

  const cancelAdd = () => {
    setAdding(false);
    setNewName("");
  };

  const save = async () => {
    const nm = newName.trim();
    if (!nm) {
      toast.error("İsim boş olamaz");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/definitions/${kind}`, { name: nm });
      onCreated?.(data);
      // Auto-select the newly created value
      onChange?.(data.name);
      setAdding(false);
      setNewName("");
      toast.success(`"${data.name}" eklendi ve seçildi`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const missing = required && !value;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">
          {label}{required && <span className="text-rose-400 ml-1">*</span>}
        </Label>
        {!adding && (
          <button
            type="button"
            data-testid={`${testId}-add-btn`}
            onClick={startAdd}
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-mono text-emerald-400 hover:text-emerald-300 hover:bg-emerald-950/40 px-1.5 py-0.5 rounded-sm transition-colors"
            title={addLabel}
          >
            <Plus className="w-3 h-3" strokeWidth={3} /> {addLabel}
          </button>
        )}
      </div>

      {adding ? (
        <div className="flex items-center gap-1.5">
          <Input
            data-testid={`${testId}-new-input`}
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); save(); }
              else if (e.key === "Escape") { e.preventDefault(); cancelAdd(); }
            }}
            placeholder={`Yeni ${label.toLowerCase()} adı...`}
            className="bg-slate-950 border-emerald-800 rounded-sm focus-visible:ring-emerald-500 font-mono h-9"
          />
          <Button
            type="button"
            data-testid={`${testId}-save-new`}
            onClick={save}
            disabled={saving}
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-500 rounded-sm h-9 px-2"
            title="Kaydet"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          </Button>
          <Button
            type="button"
            data-testid={`${testId}-cancel-new`}
            onClick={cancelAdd}
            size="sm"
            variant="outline"
            className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm h-9 px-2"
            title="İptal"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        <Select value={value || "__none"} onValueChange={(v) => onChange?.(v === "__none" ? "" : v)}>
          <SelectTrigger
            data-testid={`${testId}-trigger`}
            className={`bg-slate-950 border-slate-700 rounded-sm ${missing ? "border-rose-700" : ""}`}
          >
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-h-72">
            {items.length > 4 && (
              <div className="sticky top-0 z-10 bg-slate-900 border-b border-slate-800 p-1.5">
                <div className="relative">
                  <Search className="w-3 h-3 text-slate-500 absolute left-2 top-1/2 -translate-y-1/2" />
                  <Input
                    data-testid={`${testId}-search`}
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    placeholder="Ara…"
                    className="h-7 text-xs pl-7 bg-slate-950 border-slate-700 rounded-sm"
                  />
                </div>
              </div>
            )}
            <SelectItem value="__none">— Seçilmedi —</SelectItem>
            {filtered.map((t) => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}
            {items.length === 0 && (
              <div className="px-2 py-3 text-center text-xs text-slate-500">
                Tanımlı kayıt yok. Yukarıdaki "{addLabel}" ile ekleyin.
              </div>
            )}
            {items.length > 0 && filtered.length === 0 && (
              <div className="px-2 py-3 text-center text-xs text-slate-500 italic">
                "{q}" ile eşleşme yok.
              </div>
            )}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
